import { SSF } from 'xlsx';

export const NAME_FORMATS = ['Last, First Middle', 'First Middle Last', 'First Last'];

function pad(n) { return String(n).padStart(2, '0'); }
function toISO(y, m, d) { return `${String(y).padStart(4, '0')}-${pad(m)}-${pad(d)}`; }

// Excel serial day -> ISO via SheetJS's SSF parser, which matches Excel exactly
// (including the 1900 leap-year quirk). Only used for bare numeric date cells;
// real date cells arrive as JS Date objects (parseFile uses cellDates:true).
export function excelSerialToISO(serial) {
  if (typeof serial !== 'number' || !isFinite(serial)) return null;
  const d = SSF.parse_date_code(serial);
  if (!d || !d.y) return null;
  return toISO(d.y, d.m, d.d);
}

function fail(value, onFail) { return onFail === 'raw' ? String(value) : null; }

export function parseDate(value, { onFail = 'null', today = new Date() } = {}) {
  if (value == null || value === '') return null;
  if (value instanceof Date && !isNaN(value)) {
    // Which clock the Date is on depends on where SheetJS got it. A real .xlsx
    // date cell comes back at LOCAL midnight, but a text date in a .csv
    // ("2026-08-16") is parsed as UTC midnight — and reading local fields off
    // that lands on the previous day everywhere west of Greenwich, which is how
    // an 08-16 appointment imported as 08-15. A Date sitting exactly on UTC
    // midnight is the CSV case, so read it in UTC; anything else is a local
    // wall-clock value and is read locally. In UTC itself the two agree.
    const isUtcMidnight = value.getUTCHours() === 0 && value.getUTCMinutes() === 0
      && value.getUTCSeconds() === 0 && value.getUTCMilliseconds() === 0;
    if (isUtcMidnight) {
      return toISO(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
    }
    return toISO(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  if (typeof value === 'number') {
    return excelSerialToISO(value) || fail(value, onFail);
  }
  const str = String(value).trim();

  // ISO: YYYY-MM-DD or YYYY/MM/DD
  let m = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) {
    const [, y, mo, d] = m.map(Number);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return toISO(y, mo, d);
    return fail(value, onFail);
  }

  // M/D/Y or M-D-Y (2- or 4-digit year)
  m = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (m) {
    const mo = Number(m[1]);
    const d = Number(m[2]);
    let y = Number(m[3]);
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return fail(value, onFail);
    if (m[3].length <= 2) {
      y += 2000;
      // DOB/visit dates can't be in the future: roll back a century if needed.
      const candidate = new Date(y, mo - 1, d);
      if (candidate > today) y -= 100;
    }
    return toISO(y, mo, d);
  }

  return fail(value, onFail);
}

export function normalizePhone(value, { onFail = 'null' } = {}) {
  if (value == null || value === '') return null;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length >= 10) {
    const ten = digits.slice(-10);
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }
  if (digits.length >= 7) return digits;
  return fail(value, onFail);
}

export function combineName({ first = '', middle = '', last = '' } = {}, format) {
  const f = String(first || '').trim();
  const mid = String(middle || '').trim();
  const l = String(last || '').trim();
  if (format === 'First Middle Last') return [f, mid, l].filter(Boolean).join(' ');
  if (format === 'First Last') return [f, l].filter(Boolean).join(' ');
  // Default: 'Last, First Middle'
  const rest = [f, mid].filter(Boolean).join(' ');
  if (l && rest) return `${l}, ${rest}`;
  return l || rest;
}
